/**
 * React Query hooks for obligations. Optimistic updates keep the UI
 * snappy; on rollback the previous cache snapshot is restored.
 *
 * The optimistic data is the source of truth offline — these caches
 * are persisted to IndexedDB by the query client setup.
 */

import type {
  LifeAreaDTO,
  ObligationCreateInput,
  ObligationDTO,
  ObligationUpdateInput,
  OccasionType,
  PersonDTO,
} from '@almanac/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from './api.js';
import { mutationKeys, queryKeys } from './queryClient.js';

function newId(): string {
  // crypto.randomUUID is available in every browser we care about
  // (PROJECT.md: "PWA, polished web app, embrace web conventions").
  return crypto.randomUUID();
}

export function useLifeAreas() {
  return useQuery<LifeAreaDTO[]>({
    queryKey: queryKeys.lifeAreas,
    queryFn: api.listLifeAreas,
    staleTime: 1000 * 60 * 10,
  });
}

export function useObligations() {
  return useQuery<ObligationDTO[]>({
    queryKey: queryKeys.obligations,
    queryFn: api.listObligations,
  });
}

type ObligationListUpdater = (
  previous: ObligationDTO[] | undefined,
) => ObligationDTO[] | undefined;

function patchObligations(qc: ReturnType<typeof useQueryClient>, updater: ObligationListUpdater) {
  qc.setQueryData<ObligationDTO[]>(queryKeys.obligations, (prev) => updater(prev));
}

export function useCreateObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: mutationKeys.createObligation,
    mutationFn: (input: ObligationCreateInput & { id: string }) => api.createObligation(input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: queryKeys.obligations });
      const previous = qc.getQueryData<ObligationDTO[]>(queryKeys.obligations);
      const now = new Date().toISOString();
      const optimistic: ObligationDTO = {
        id: input.id,
        title: input.title,
        description: input.description ?? null,
        lifeAreaId: input.lifeAreaId,
        dueDate: input.dueDate,
        dueTime: input.kind === 'occasion' ? null : (input.dueTime ?? null),
        nextDueDate: input.dueDate,
        recurrenceRule: input.recurrenceRule ?? null,
        priority: input.priority ?? 'medium',
        status: 'active',
        reminderDaysBefore: input.reminderDaysBefore ?? null,
        notes: input.notes ?? null,
        visibility: input.visibility ?? 'private',
        kind: input.kind ?? 'task',
        occasionType: input.occasionType ?? null,
        occasionLabel: input.occasionLabel ?? null,
        anchorYear: input.anchorYear ?? null,
        suggestionDismissedFor: input.suggestionDismissedFor ?? null,
        tripId: input.tripId ?? null,
        personIds: input.personIds ?? [],
        createdBy: 'me',
        createdAt: now,
        updatedAt: now,
        completedAt: null,
      };
      patchObligations(qc, (prev) => [...(prev ?? []), optimistic]);
      return { previous };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.obligations, ctx.previous);
    },
    onSuccess: (server) => {
      patchObligations(qc, (prev) =>
        (prev ?? []).map((o) => (o.id === server.id ? server : o)),
      );
    },
  });
}

export function useUpdateObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: mutationKeys.updateObligation,
    mutationFn: (vars: { id: string; input: ObligationUpdateInput }) =>
      api.updateObligation(vars.id, vars.input),
    onMutate: async ({ id, input }) => {
      await qc.cancelQueries({ queryKey: queryKeys.obligations });
      const previous = qc.getQueryData<ObligationDTO[]>(queryKeys.obligations);
      patchObligations(qc, (prev) =>
        (prev ?? []).map((o) =>
          o.id === id
            ? {
                ...o,
                ...input,
                description: input.description === undefined ? o.description : (input.description ?? null),
                dueTime: input.dueTime === undefined ? o.dueTime : (input.dueTime ?? null),
                recurrenceRule:
                  input.recurrenceRule === undefined ? o.recurrenceRule : (input.recurrenceRule ?? null),
                reminderDaysBefore:
                  input.reminderDaysBefore === undefined
                    ? o.reminderDaysBefore
                    : (input.reminderDaysBefore ?? null),
                notes: input.notes === undefined ? o.notes : (input.notes ?? null),
                suggestionDismissedFor:
                  input.suggestionDismissedFor === undefined
                    ? o.suggestionDismissedFor
                    : (input.suggestionDismissedFor ?? null),
                updatedAt: new Date().toISOString(),
              }
            : o,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.obligations, ctx.previous);
    },
    onSuccess: (server) => {
      patchObligations(qc, (prev) =>
        (prev ?? []).map((o) => (o.id === server.id ? server : o)),
      );
    },
  });
}

export function useCompleteObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: mutationKeys.completeObligation,
    mutationFn: (vars: { id: string; completedAt?: string }) =>
      api.completeObligation(vars.id, vars.completedAt),
    onMutate: async ({ id, completedAt }) => {
      await qc.cancelQueries({ queryKey: queryKeys.obligations });
      const previous = qc.getQueryData<ObligationDTO[]>(queryKeys.obligations);
      const now = completedAt ?? new Date().toISOString();
      patchObligations(qc, (prev) =>
        (prev ?? []).map((o) => {
          if (o.id !== id) return o;
          const isRecurring = Boolean(o.recurrenceRule);
          // Bump nextDueDate optimistically so the row sorts at its
          // post-completion position immediately — same as the server
          // will compute on the round trip. Without this the cache
          // briefly shows the row at its old (today's) date and it
          // can drift to the top of the list before the server
          // response settles. The bump uses simple cadence math for
          // the four standard cadences (DAILY/WEEKLY/MONTHLY/YEARLY)
          // and falls back to +1 day for anything else.
          const nextDueDate = isRecurring && o.recurrenceRule
            ? bumpDueDateForRecurrence(o.recurrenceRule, now.slice(0, 10))
            : o.nextDueDate;
          return {
            ...o,
            status: isRecurring ? 'active' : 'completed',
            completedAt: now,
            nextDueDate,
            updatedAt: now,
          };
        }),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.obligations, ctx.previous);
    },
    onSuccess: (server) => {
      patchObligations(qc, (prev) =>
        (prev ?? []).map((o) => (o.id === server.id ? server : o)),
      );
    },
  });
}

export function useReopenObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: mutationKeys.reopenObligation,
    mutationFn: (vars: { id: string; nextDueDate?: string }) =>
      api.reopenObligation(vars.id, vars.nextDueDate),
    onMutate: async ({ id, nextDueDate }) => {
      await qc.cancelQueries({ queryKey: queryKeys.obligations });
      const previous = qc.getQueryData<ObligationDTO[]>(queryKeys.obligations);
      const now = new Date().toISOString();
      patchObligations(qc, (prev) =>
        (prev ?? []).map((o) =>
          o.id === id
            ? {
                ...o,
                status: 'active',
                completedAt: null,
                nextDueDate: nextDueDate ?? o.dueDate,
                updatedAt: now,
              }
            : o,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.obligations, ctx.previous);
    },
    onSuccess: (server) => {
      patchObligations(qc, (prev) =>
        (prev ?? []).map((o) => (o.id === server.id ? server : o)),
      );
    },
  });
}

export function useDeleteObligation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: mutationKeys.deleteObligation,
    mutationFn: (vars: { id: string }) => api.deleteObligation(vars.id),
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: queryKeys.obligations });
      const previous = qc.getQueryData<ObligationDTO[]>(queryKeys.obligations);
      patchObligations(qc, (prev) => (prev ?? []).filter((o) => o.id !== id));
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKeys.obligations, ctx.previous);
    },
  });
}

export { newId };

/**
 * Find an existing, non-deleted occasion of the given type already
 * linked to this person. Used to prevent the same birthday being
 * captured twice. `excludeId` lets the edit drawer exclude the row
 * currently being edited from the check.
 */
/**
 * Sort key combining `nextDueDate` (or `dueDate` fallback) with
 * the optional `dueTime`. Lets two items on the same day order
 * by their time-of-day — a 9 AM commitment surfaces above an
 * 11 AM one. Untimed items sort after timed ones on the same date
 * via the `99:99` sentinel, since "anytime today" has no specific
 * deadline within the day.
 *
 * The shape `YYYY-MM-DDTHH:MM` is a valid lex sort under
 * `localeCompare`, so callers just compare the strings directly.
 */
export function obligationSortKey(o: ObligationDTO): string {
  const date = (o.nextDueDate ?? o.dueDate).slice(0, 10);
  const time = o.dueTime ?? '99:99';
  return `${date}T${time}`;
}

/**
 * Compute the next-occurrence date locally for the four standard
 * cadences. Lets the client mutation bump `nextDueDate`
 * optimistically — without it, the cache briefly carries the
 * stale "still today" value and a just-completed recurring row can
 * sort at the top of the list before the server response arrives
 * (which, if the user is offline or the network stalls, may never
 * happen). The result is a date-only `YYYY-MM-DD` string.
 *
 * The four cadences the quick-add form supports map to simple date
 * arithmetic anchored on the completion date — no need to pull in
 * the full RRULE library client-side just for the optimistic
 * bump. Custom RRULEs fall back to a `+1 day` conservative bump,
 * which at minimum gets the row out of the "still today" sort
 * position; the server response then corrects to the exact next
 * occurrence within milliseconds.
 */
/* ── Recurrence end conditions ───────────────────────────────────────
   The five standard cadences are open-ended; an end condition bounds
   them — "ends after N occurrences" (RRULE COUNT) or "ends on a date"
   (RRULE UNTIL, stored as end-of-day UTC so the end date is
   inclusive). The server needs no special handling: the rrule library
   honours both, computeNextDueDate returns null past the end, and the
   complete route closes the obligation out. */

export type RecurrenceEnd =
  | { kind: 'never' }
  | { kind: 'count'; count: number }
  | { kind: 'until'; date: string }; // YYYY-MM-DD, inclusive

/** Pull COUNT/UNTIL out of a rule: the bare cadence + the end. */
export function splitRecurrenceEnd(rule: string): { base: string; end: RecurrenceEnd } {
  const trimmed = rule.trim().replace(/^RRULE:/u, '');
  let end: RecurrenceEnd = { kind: 'never' };
  const kept: string[] = [];
  for (const part of trimmed.split(';').filter(Boolean)) {
    const eq = part.indexOf('=');
    const key = (eq > 0 ? part.slice(0, eq) : part).toUpperCase();
    const value = eq > 0 ? part.slice(eq + 1) : '';
    if (key === 'COUNT' && /^\d+$/.test(value)) {
      end = { kind: 'count', count: Number(value) };
    } else if (key === 'UNTIL' && /^\d{8}/.test(value)) {
      end = {
        kind: 'until',
        date: `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`,
      };
    } else {
      kept.push(part);
    }
  }
  return { base: kept.join(';'), end };
}

/** Compose a rule from a bare cadence and an end condition. */
export function withRecurrenceEnd(base: string, end: RecurrenceEnd): string {
  if (end.kind === 'count' && end.count > 0) return `${base};COUNT=${end.count}`;
  if (end.kind === 'until' && end.date) {
    // End-of-day UTC so "ends on 31 Dec" still fires on 31 Dec.
    return `${base};UNTIL=${end.date.replace(/-/gu, '')}T235959Z`;
  }
  return base;
}

/** Prose suffix for the preview card: "ends after 10 times" /
 *  "until 31 Dec 2026"; null when open-ended. */
export function formatRecurrenceEnd(end: RecurrenceEnd): string | null {
  if (end.kind === 'count') {
    return `ends after ${end.count} ${end.count === 1 ? 'time' : 'times'}`;
  }
  if (end.kind === 'until') {
    const d = new Date(`${end.date}T00:00:00`);
    const pretty = Number.isNaN(d.getTime())
      ? end.date
      : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    return `until ${pretty}`;
  }
  return null;
}

export function bumpDueDateForRecurrence(rule: string, fromDate: string): string {
  // End parts (COUNT/UNTIL) don't change the step size — strip them so
  // the exact-match cadence branches below still hit.
  const { base: cleaned } = splitRecurrenceEnd(rule);
  const datePart = fromDate.slice(0, 10);
  const d = new Date(`${datePart}T00:00:00`);
  if (Number.isNaN(d.getTime())) return datePart;
  if (cleaned === 'FREQ=DAILY') {
    d.setDate(d.getDate() + 1);
  } else if (cleaned === 'FREQ=WEEKLY') {
    d.setDate(d.getDate() + 7);
  } else if (cleaned === 'FREQ=MONTHLY') {
    d.setMonth(d.getMonth() + 1);
  } else if (cleaned.startsWith('FREQ=YEARLY')) {
    // Covers both the simple `FREQ=YEARLY` rule and the anchored
    // birthday/anniversary form `FREQ=YEARLY;BYMONTH=N;BYMONTHDAY=M`,
    // since +1 year from the anchor date is the same calendar day
    // either way.
    d.setFullYear(d.getFullYear() + 1);
  } else {
    d.setDate(d.getDate() + 1);
  }
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Render a stored `HH:MM` value in the user's preferred locale —
 * `3:00 PM` in the US, `15:00` in most of Europe. Returns the raw
 * string back on parse failure so a stray malformed value can never
 * crash the row it's rendered on.
 */
export function formatTimeForDisplay(hhmm: string | null | undefined): string {
  if (!hhmm) return '';
  const [hStr, mStr] = hhmm.split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/**
 * Wall-clock `Date.now()` that re-publishes once a minute (plus on
 * tab focus and visibility regain). Lets time-sensitive UI — the
 * red-overdue label on rows whose timed due moment has just
 * passed — react to the clock without each row spinning up its own
 * interval.
 *
 * The visibilitychange + focus listeners cover the case where iOS
 * throttles `setInterval` for a backgrounded PWA; returning to the
 * tab forces an immediate update so the row's state catches up.
 */
export function useNowMinute(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    function update() {
      setNow(Date.now());
    }
    const id = window.setInterval(update, 60_000);
    window.addEventListener('focus', update);
    document.addEventListener('visibilitychange', update);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', update);
      document.removeEventListener('visibilitychange', update);
    };
  }, []);
  return now;
}

/**
 * Time-aware "is this past its moment" check.
 *
 *  - Untimed (all-day) items go overdue at end-of-day, so a task
 *    due "today" with no time stays current until tomorrow.
 *  - Timed items go overdue at the exact `HH:MM` set on them — so
 *    `today 10:00 AM` is overdue at 10:00:01 AM the same day.
 *  - Completed rows are never overdue.
 *
 * `now` is supplied (rather than read fresh inside) so the caller
 * can subscribe to `useNowMinute()` once and pass the same value
 * to multiple format helpers — keeps the function pure and easy
 * to reason about in tests.
 */
export function isOverdue(o: ObligationDTO, now: number): boolean {
  if (o.status === 'completed') return false;
  const due = o.nextDueDate ?? o.dueDate;
  if (!due) return false;
  const datePart = due.slice(0, 10);
  const timePart = o.dueTime ?? '23:59';
  const target = new Date(`${datePart}T${timePart}:00`);
  if (Number.isNaN(target.getTime())) return false;
  return target.getTime() < now;
}

/**
 * Joins person names for an occasion's title. Single → "Oscar",
 * pair → "Oscar & Mihaela", three+ → Oxford-style with a final "&".
 *
 * Lives here (not inline in components) because it's the same logic
 * used at create time, edit time, and render time — having a single
 * implementation means a name change in any one place can't drift.
 */
export function joinPersonNames(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

/**
 * Resolve the live, currently-correct display title of an obligation.
 *
 * For occasions: derive from the linked people's *current* names so
 * renaming a person propagates instantly without an obligation rewrite.
 * The stored `o.title` is a snapshot — useful for audit logs, used as
 * a fallback only when the linked people aren't loaded yet or have
 * been soft-deleted.
 *
 * For tasks: the stored title is the only title; return it verbatim.
 */
export function liveObligationTitle(
  o: ObligationDTO,
  people: PersonDTO[] | undefined,
): string {
  if (o.kind !== 'occasion') return o.title;
  if (!people || o.personIds.length === 0) return o.title;
  const names = o.personIds
    .map((id) => people.find((p) => p.id === id)?.displayName)
    .filter((n): n is string => Boolean(n));
  if (names.length === 0) return o.title;
  return joinPersonNames(names);
}

/**
 * Case-insensitive substring search across the user-facing text of
 * an obligation. Matches against the live title (which includes
 * person names for occasions), description, notes, and the
 * occasion label, so a user can find a row by any word that's
 * meaningful to them — including a person whose name lives in the
 * `people` table rather than on the obligation directly.
 *
 * The query is trimmed but otherwise unprocessed — no fuzzy or
 * stem matching at this scale. The personal-list size of this app
 * means substring matching is plenty; fuzzy would mostly just
 * produce false positives.
 */
export function obligationMatchesQuery(
  o: ObligationDTO,
  query: string,
  people: PersonDTO[] | undefined,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    liveObligationTitle(o, people),
    o.description,
    o.notes,
    o.occasionLabel,
  ]
    .filter((s): s is string => Boolean(s))
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

/**
 * True when the obligation was marked complete earlier today (in the
 * local timezone). Drives the "Acknowledged today" presentation for
 * recurring items, where the row otherwise looks identical the moment
 * after the check is tapped — the next occurrence is already on the
 * far side of a 12-month bump.
 */
export function isAcknowledgedToday(o: ObligationDTO): boolean {
  if (!o.completedAt) return false;
  const completed = new Date(o.completedAt);
  if (Number.isNaN(completed.getTime())) return false;
  const today = new Date();
  return (
    completed.getFullYear() === today.getFullYear() &&
    completed.getMonth() === today.getMonth() &&
    completed.getDate() === today.getDate()
  );
}

export function findExistingOccasion(
  obligations: ObligationDTO[] | undefined,
  personId: string | null,
  occasionType: OccasionType,
  excludeId?: string,
): ObligationDTO | null {
  if (!obligations || !personId) return null;
  for (const o of obligations) {
    if (excludeId && o.id === excludeId) continue;
    if (o.kind !== 'occasion') continue;
    if (o.occasionType !== occasionType) continue;
    if (!o.personIds.includes(personId)) continue;
    return o;
  }
  return null;
}

/* ── Occasion → task suggestion ──────────────────────────────────────
   A birthday/anniversary coming up within the horizon invites a
   one-tap "buy a gift / plan something" task from its preview card —
   the same record-spawns-an-obligation pattern as the documents
   renewal-task action. The dismissal is pinned to the occurrence it
   applies to, so the acknowledgment bump to next year re-arms the
   prompt on its own. */

/** How far out (days) an occasion starts inviting a reminder task. */
export const OCCASION_SUGGEST_DAYS = 30;

/** The occurrence a suggestion (and its dismissal) applies to. */
export function occasionOccurrenceKey(o: ObligationDTO): string {
  return (o.nextDueDate ?? o.dueDate).slice(0, 10);
}

/** True when the user already dismissed the prompt for the occasion's
 *  current occurrence — quietly or by creating the task. */
export function isOccasionSuggestionHandled(o: ObligationDTO): boolean {
  return o.suggestionDismissedFor === occasionOccurrenceKey(o);
}

/** Build the one-tap reminder task for an occasion — shared by the
 *  preview-card strip and the Today/Obligations banner so every entry
 *  point creates the identical task. Due a week before the occurrence
 *  (gift-buying lead), clamped to today when the date is closer.
 *  `liveTitle` is the occasion's display title (the person's name). */
export function occasionReminderDraft(
  o: ObligationDTO,
  liveTitle: string,
  now: Date = new Date(),
): ObligationCreateInput & { id: string } {
  const key = occasionOccurrenceKey(o);
  const lead = new Date(`${key}T00:00:00`);
  lead.setDate(lead.getDate() - 7);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const due = lead < today ? today : lead;
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  const dueIso = `${due.getFullYear()}-${pad(due.getMonth() + 1)}-${pad(due.getDate())}`;
  const isBirthday = o.occasionType !== 'anniversary';
  const label = o.occasionLabel?.trim();
  const occasionName = isBirthday
    ? `${liveTitle}’s birthday`
    : `the ${(label || 'anniversary').toLowerCase()}${label ? ' anniversary' : ''}`;
  const when = new Date(`${key}T00:00:00`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return {
    id: crypto.randomUUID(),
    title: isBirthday ? `Buy a gift for ${liveTitle}` : `Plan something for ${liveTitle}`,
    description: `For ${occasionName} on ${when}.`,
    lifeAreaId: o.lifeAreaId,
    dueDate: dueIso,
    recurrenceRule: null,
    priority: 'medium',
    reminderDaysBefore: null,
    notes: null,
    visibility: o.visibility,
    kind: 'task',
    occasionType: null,
    occasionLabel: null,
    anchorYear: null,
    personIds: o.personIds,
  };
}

/** Whether the suggestion strip shows at all: an active occasion whose
 *  next occurrence is between today and the horizon. */
export function occasionSuggestionDue(o: ObligationDTO, now: Date): boolean {
  if (o.kind !== 'occasion' || o.status === 'completed') return false;
  const key = occasionOccurrenceKey(o);
  if (!key) return false;
  const target = new Date(`${key}T00:00:00`);
  if (Number.isNaN(target.getTime())) return false;
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const days = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  return days >= 0 && days <= OCCASION_SUGGEST_DAYS;
}

// ── Perspectives (the Obligations page's Focus / Areas / People views) ──

/** How many distinct life-area accent tokens exist in tokens.css.
 *  Areas take a colour by their position in the (sort-ordered) list,
 *  cycling when there are more areas than colours. */
export const AREA_COLOR_COUNT = 6;

export function areaColorVar(index: number): string {
  return `var(--area-${(index % AREA_COLOR_COUNT) + 1})`;
}

function dueOf(o: ObligationDTO): string {
  return (o.nextDueDate ?? o.dueDate)?.slice(0, 10) ?? '';
}

function isoDay(d: Date): string {
  const p = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export interface FocusGroups {
  overdue: ObligationDTO[];
  thisWeek: ObligationDTO[];
  later: ObligationDTO[];
}

/** The Focus view's horizon sections. Tasks only — occasions live in
 *  the strip above — sorted by due date within each section. "This
 *  week" is today through seven days out; undated tasks land in
 *  "Later" (they have no horizon to pin them to). */
export function groupForFocus(rows: ObligationDTO[], now: Date): FocusGroups {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const todayIso = isoDay(today);
  const week = new Date(today);
  week.setDate(week.getDate() + 7);
  const weekIso = isoDay(week);
  const groups: FocusGroups = { overdue: [], thisWeek: [], later: [] };
  for (const o of rows) {
    if (o.status === 'completed' || o.kind === 'occasion') continue;
    const due = dueOf(o);
    if (due && due < todayIso) groups.overdue.push(o);
    else if (due && due <= weekIso) groups.thisWeek.push(o);
    else groups.later.push(o);
  }
  const byDue = (a: ObligationDTO, b: ObligationDTO) =>
    obligationSortKey(a).localeCompare(obligationSortKey(b));
  groups.overdue.sort(byDue);
  groups.thisWeek.sort(byDue);
  groups.later.sort(byDue);
  return groups;
}

/** The next few occasions for the Focus view's strip — soonest first,
 *  within the coming year so a strip card always means "coming up". */
export function upcomingOccasions(rows: ObligationDTO[], now: Date, limit = 5): ObligationDTO[] {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const horizon = new Date(today);
  horizon.setFullYear(horizon.getFullYear() + 1);
  const horizonIso = isoDay(horizon);
  const todayIso = isoDay(today);
  return rows
    .filter((o) => {
      if (o.status === 'completed' || o.kind !== 'occasion') return false;
      const due = dueOf(o);
      return due !== '' && due >= todayIso && due <= horizonIso;
    })
    .sort((a, b) => dueOf(a).localeCompare(dueOf(b)))
    .slice(0, limit);
}

export interface MonthProgress {
  /** Month name, e.g. "July". */
  label: string;
  done: number;
  total: number;
}

/** "July · 2 of 6 done" — how the current month's tasks are going.
 *  Total counts active tasks due this month plus tasks completed this
 *  month (however they were dated); done is the completed count.
 *  Occasions don't count — acknowledging a birthday isn't progress.
 *  Null when the month holds nothing, so the bar can stay away. */
export function monthProgress(rows: ObligationDTO[], now: Date): MonthProgress | null {
  const y = now.getFullYear();
  const m = now.getMonth();
  const prefix = `${y}-${m + 1 < 10 ? `0${m + 1}` : m + 1}`;
  let done = 0;
  let pending = 0;
  for (const o of rows) {
    if (o.kind === 'occasion') continue;
    if (o.status === 'completed') {
      if ((o.completedAt ?? '').slice(0, 7) === prefix) done += 1;
    } else if (dueOf(o).slice(0, 7) === prefix) {
      pending += 1;
    }
  }
  const total = done + pending;
  if (total === 0) return null;
  return { label: now.toLocaleDateString(undefined, { month: 'long' }), done, total };
}

export interface AreaGroup {
  areaId: string;
  label: string;
  /** Index into the life-area colour cycle (the area's list position). */
  colorIndex: number;
  rows: ObligationDTO[];
}

/** The Areas view: active tasks grouped by life area, areas in their
 *  canonical (sort) order, empty areas dropped, rows by due date.
 *  Occasions stay out — nearly all of them file under Personal, which
 *  would flatten that group back into the pile this view exists to
 *  avoid. They cluster separately via activeOccasionsByDue below. */
export function groupByArea(
  rows: ObligationDTO[],
  areas: { id: string; name: string }[],
): AreaGroup[] {
  const byDue = (a: ObligationDTO, b: ObligationDTO) =>
    obligationSortKey(a).localeCompare(obligationSortKey(b));
  const active = rows.filter((o) => o.status !== 'completed' && o.kind !== 'occasion');
  return areas
    .map((area, i) => ({
      areaId: area.id,
      label: area.name,
      colorIndex: i,
      rows: active.filter((o) => o.lifeAreaId === area.id).sort(byDue),
    }))
    .filter((g) => g.rows.length > 0);
}

/** Every active occasion, soonest first — the Areas view's trailing
 *  "Birthdays & anniversaries" cluster. */
export function activeOccasionsByDue(rows: ObligationDTO[]): ObligationDTO[] {
  return rows
    .filter((o) => o.status !== 'completed' && o.kind === 'occasion')
    .sort((a, b) => dueOf(a).localeCompare(dueOf(b)));
}

export interface PersonGroup {
  personId: string;
  label: string;
  rows: ObligationDTO[];
}

/** The People view: active occasions grouped under each linked person
 *  (a shared anniversary lists under both), people ordered by whose
 *  date comes soonest. Tasks don't carry person links, so this view is
 *  the household's dates registry rather than a task list. */
export function groupByPerson(
  rows: ObligationDTO[],
  people: { id: string; displayName: string }[],
): PersonGroup[] {
  const nameById = new Map(people.map((p) => [p.id, p.displayName]));
  const groups = new Map<string, PersonGroup>();
  const occasions = rows
    .filter((o) => o.status !== 'completed' && o.kind === 'occasion')
    .sort((a, b) => dueOf(a).localeCompare(dueOf(b)));
  for (const o of occasions) {
    for (const pid of o.personIds) {
      const label = nameById.get(pid);
      if (!label) continue;
      let g = groups.get(pid);
      if (!g) {
        g = { personId: pid, label, rows: [] };
        groups.set(pid, g);
      }
      g.rows.push(o);
    }
  }
  return [...groups.values()].sort((a, b) =>
    dueOf(a.rows[0]!).localeCompare(dueOf(b.rows[0]!)),
  );
}
